import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
} from '@mui/material';
import { Bell } from 'lucide-react';
import { acme, acmeIconTones } from '../../theme/acmeTheme';
import { isAdminRole, isManagerRole } from '../../utils/userRoles';
import { Table, Button, Card, CardContent } from '../ui';

export function notificationTriggerLabel(triggeredBy) {
  if (!triggeredBy) return 'System';
  if (isAdminRole(triggeredBy.role)) return 'Admin';
  if (isManagerRole(triggeredBy.role)) return 'Manager';
  return triggeredBy.login ||
    `${triggeredBy.firstName || ''} ${triggeredBy.lastName || ''}`.trim();
}

export function notificationAccentColors(isUnread) {
  return {
    loginColor: isUnread ? acme.colors.foreground : acme.colors.iconDefault,
    projectColor: isUnread ? acme.colors.iconEmphasis : acme.colors.mutedForegroundSolid,
  };
}

export function NotificationsEmptyState() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: 6,
        px: 2,
        maxWidth: 400,
        mx: 'auto',
      }}
    >
      <Bell size={48} strokeWidth={1.5} color={acmeIconTones.muted} />
      <Typography sx={{ fontWeight: 600, color: acme.colors.foreground, mt: 2, mb: 0.5 }}>
        No notifications yet
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', color: acme.colors.mutedForegroundSolid }}>
        You&apos;ll see project updates and system notifications here
      </Typography>
    </Box>
  );
}

export function NotificationMobileCard({ notification: n, onMarkViewed }) {
  const isUnread = !n.isRead;
  const { loginColor, projectColor } = notificationAccentColors(isUnread);
  const author = notificationTriggerLabel(n.triggeredBy);

  return (
    <Card sx={{ mb: 2, boxShadow: 'none', borderLeft: isUnread ? `3px solid ${acme.colors.foreground}` : undefined }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Avatar
          src={n.triggeredBy?.avatarUrl || '/images/user.png'}
          sx={{ width: 32, height: 32 }}
        />
        <Typography sx={{ color: loginColor, fontWeight: 600, fontSize: '0.875rem' }}>
          {author}
        </Typography>
      </Box>

      <Typography sx={{ color: projectColor, fontWeight: 600, mb: 1, fontSize: '0.875rem' }}>
        {n.projectName}
      </Typography>

      <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 1, fontSize: '0.875rem' }}>
        {n.changes}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography sx={{ color: acme.colors.mutedForegroundSolid, fontSize: '0.75rem' }}>
          {new Date(n.createdAt).toLocaleString()}
        </Typography>
        <Button variant="outline" size="sm" onClick={() => onMarkViewed(n._id)}>
          Viewed
        </Button>
      </Box>
      </CardContent>
    </Card>
  );
}

export function NotificationsDataTable({ displayed, onMarkViewed }) {
  return (
    <Table containerSx={{ boxShadow: 'none', border: 'none' }}>
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell>Author</TableCell>
          <TableCell>Project</TableCell>
          <TableCell>Changes</TableCell>
          <TableCell>Time</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {displayed.map((n) => {
          const isUnread = !n.isRead;
          const { loginColor, projectColor } = notificationAccentColors(isUnread);
          const author = notificationTriggerLabel(n.triggeredBy);
          return (
            <TableRow key={n._id}>
              <TableCell sx={{ width: 24 }}>
                {isUnread && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: acme.colors.foreground,
                    }}
                  />
                )}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar
                    src={n.triggeredBy?.avatarUrl || '/images/user.png'}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Typography sx={{ color: loginColor, fontWeight: 600, fontSize: '0.875rem' }}>
                    {author}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography sx={{ color: projectColor, fontWeight: 600, fontSize: '0.875rem' }}>
                  {n.projectName}
                </Typography>
              </TableCell>
              <TableCell sx={{ color: acme.colors.mutedForegroundSolid, fontSize: '0.875rem' }}>
                {n.changes}
              </TableCell>
              <TableCell sx={{ color: acme.colors.mutedForegroundSolid, fontSize: '0.875rem' }}>
                {new Date(n.createdAt).toLocaleString()}
              </TableCell>
              <TableCell>
                <Button variant="outline" size="sm" onClick={() => onMarkViewed(n._id)}>
                  Viewed
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
